import moment from 'moment'
import { parseFieldToMomentIfNeeded } from '../reducers/parser'
import { removeUndefinedFromObject } from '../utils/url'
import {
  createDateRange,
  createOperationTimeBoundary,
  createDailyResetBoundary,
} from '../utils/dateUtils'

let store
let jwt

export const initAPI = (_store) => {
  store = _store
  store.subscribe(stateListener)
}

export const stateListener = () => {
  jwt =
    store.getState().auth && store.getState().auth.jwt
      ? store.getState().auth.jwt
      : undefined
}

/**
 * Transform query parameters to use timezone-aware date ranges
 * Converts 'daily' and 'operationDaily' flags to explicit date/time parameters
 * @param {Object} query - Original query object
 * @returns {Object} Transformed query object
 */
const transformQueryWithTimezone = (query) => {
  if (!query) return query

  const transformed = { ...query }

  // Handle 'daily' flag - convert to startDate/endDate for today in user's timezone
  // This filters by serviceDateTime field
  if (query.daily === true) {
    // For summary reports, use 3 AM to 3 AM boundary instead of midnight to midnight
    if (query.summaryReport === true) {
      const boundary = createDailyResetBoundary()
      transformed.startDate = boundary.start
      transformed.endDate = boundary.end
    } else {
      const dateRange = createDateRange(moment())
      transformed.startDate = dateRange.start
      transformed.endDate = dateRange.end
    }
    delete transformed.daily
  }

  // Handle 'operationDaily' flag - convert to serviceInfoInTime_gte/serviceInfoInTime_lte
  // This filters by serviceInfo.inTime field (check-in time) with 12-hour boundary
  if (query.operationDaily === true) {
    const timeBoundary = createOperationTimeBoundary(12)
    transformed.serviceInfoInTime_gte = timeBoundary.start
    transformed.serviceInfoInTime_lte = timeBoundary.end
    delete transformed.operationDaily
  }

  return transformed
}

const withAuthFetch = (func) => {
  return func(createAuthFetch())
}

const createAuthFetch = () => (url, options) => {
  if (!jwt) return Promise.reject('Authentication token is not found')

  return fetch(url, {
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    ...options,
  })
}

export const fetchServiceTxns = withAuthFetch((authFetch) => (query) => {
  // Transform query to use timezone-aware date ranges
  const transformedQuery = transformQueryWithTimezone(query)
  return authFetch(
    `/api/serviceTxns?${new URLSearchParams(removeUndefinedFromObject(transformedQuery))}`,
  )
    .then((data) => data.json())
    .then((serviceTxns) =>
      serviceTxns.map((s) => ({
        ...s,
        serviceDateTime: s.serviceDateTime
          ? moment(s.serviceDateTime)
          : undefined,
      })),
    )
})

export const fetchReservations = withAuthFetch((authFetch) => (query) => {
  // Transform query to use timezone-aware date ranges
  const transformedQuery = transformQueryWithTimezone(query)
  return authFetch(
    `/api/reservations?${new URLSearchParams(removeUndefinedFromObject(transformedQuery))}`,
  )
    .then((data) => data.json())
    .then((data) => parseFieldToMomentIfNeeded(data))
})

export const fetchAirlines = withAuthFetch(
  (authFetch) => (query) =>
    authFetch(
      `/api/airlines?${new URLSearchParams(removeUndefinedFromObject(query))}`,
    ).then((data) => data.json()),
)

export const fetchAirlineById = withAuthFetch(
  (authFetch) => (id) =>
    authFetch(`/api/airlines/${id}`).then((data) => data.json()),
)

export const fetchCardById = withAuthFetch(
  (authFetch) => (id) =>
    authFetch(`/api/cards/${id}`).then((data) => data.json()),
)

export const fetchProductPrice = withAuthFetch(
  (authFetch) => (id, serviceTxn) =>
    authFetch(`/api/products/${id}/price`, {
      method: 'PUT',
      body: JSON.stringify(serviceTxn),
    }).then((data) => data.json()),
)
